import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, RotateCcw, ShieldAlert } from "lucide-react";
import { Txt } from "@/components/Txt";
import { useContent } from "@/context/ContentContext";

export default function ResetData() {
  const { editMode } = useContent();
  const [types, setTypes] = useState([]);
  const [selected, setSelected] = useState({});
  const [confirmation, setConfirmation] = useState("");
  const [deleteAllConfirm, setDeleteAllConfirm] = useState("");

  const load = useCallback(async () => {
    const r = await api.get("/reset/types");
    setTypes(r.data.types);
  }, []);
  useEffect(() => { load(); }, [load]);

  const anySelected = Object.values(selected).some(Boolean);

  const reset = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length === 0) return toast.error("Choose at least one record type.");
    if (confirmation.trim().toUpperCase() !== "RESET") return toast.error('Type RESET to confirm.');
    try {
      const r = await api.post("/reset", { type_ids: ids, confirmation });
      toast.success("Selected test data wiped.");
      setSelected({}); setConfirmation("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reset"); }
  };

  const deleteAll = async () => {
    if (deleteAllConfirm.trim().toUpperCase() !== "DELETE ALL") return toast.error('Type DELETE ALL to confirm.');
    try {
      await api.post("/reset/delete-all", { confirmation: deleteAllConfirm });
      toast.success("All data deleted. Member numbering reset to the start.");
      setDeleteAllConfirm("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete all data"); }
  };

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-11 h-11 rounded-xl bg-maroon text-cream flex items-center justify-center"><RotateCcw className="w-5 h-5" /></div>
        <h1 className="font-zilla text-3xl sm:text-4xl font-semibold"><Txt id="reset.title" screen="Reset system data">Reset system data</Txt></h1>
      </div>
      <p className="text-[17px] text-ink/70 mb-5">
        <Txt id="reset.subtitle" screen="Reset system data">Wipe records by type before go-live. This is meant for clearing out test data generated during the build. It permanently removes the selected records — it does not use Trash.</Txt>
      </p>

      <div className="bg-maroon/10 border border-maroon/30 rounded-lg p-4 mb-5 flex gap-3">
        <AlertTriangle className="w-5 h-5 text-maroon shrink-0 mt-0.5" />
        <p className="text-[15px] text-ink/80"><Txt id="reset.warning" screen="Reset system data">This cannot be undone. Records removed here are gone for good.</Txt></p>
      </div>

      <div className="bg-cream border border-ink/15 rounded-xl p-5 mb-5">
        <div className="space-y-1" data-testid="reset-types-list">
          {types.map((t) => (
            <label key={t.id} className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-warm-cream cursor-pointer min-h-[44px]" data-testid={`reset-type-${t.id}`}>
              <input type="checkbox" checked={!!selected[t.id]}
                onChange={(e) => setSelected({ ...selected, [t.id]: e.target.checked })}
                className="w-5 h-5 accent-maroon" />
              <span className="text-[15px] font-medium flex-1">{t.label}</span>
              <span className="text-[14px] text-ink/55">{t.count} {t.count === 1 ? "record" : "records"}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="bg-cream border border-ink/15 rounded-xl p-5">
        <label className="text-[14px] font-semibold">Type <span className="font-mono text-maroon">RESET</span> to confirm</label>
        <input data-testid="reset-confirmation-input" value={confirmation} onChange={(e) => setConfirmation(e.target.value)}
          placeholder="RESET" className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px] mt-2 mb-4" />
        <button data-testid="reset-submit-button" onClick={editMode ? undefined : reset}
          disabled={editMode ? false : (!anySelected || confirmation.trim().toUpperCase() !== "RESET")}
          className="bg-maroon text-cream font-semibold rounded-lg px-6 py-3 min-h-[48px] text-[15px] disabled:opacity-40">
          <Txt id="reset.confirm_btn" screen="Reset system data">Permanently wipe selected data</Txt>
        </button>
      </div>

      <div className="mt-10 border-2 border-maroon rounded-xl overflow-hidden" data-testid="delete-all-card">
        <div className="bg-maroon text-cream px-5 py-4 flex items-center gap-3">
          <ShieldAlert className="w-6 h-6 shrink-0" />
          <div>
            <h2 className="font-zilla text-2xl font-semibold leading-tight"><Txt id="reset.deleteall.title" screen="Reset system data">Delete all data</Txt></h2>
            <p className="text-[14px] text-cream/80"><Txt id="reset.deleteall.tagline" screen="Reset system data">The most absolute wipe. Separate from everything above.</Txt></p>
          </div>
        </div>
        <div className="p-5">
          <p className="text-[15px] text-ink/80 mb-3">
            This irreversibly erases <strong>every record in the system at once</strong> and empties Trash — nothing
            is given a 30-day recovery window. It is meant only for clearing mock or test data during the build,
            never for real production removal, cancellation, or non-payment.
          </p>
          <div className="bg-warm-cream border border-maroon/30 rounded-lg p-4 mb-4 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-maroon shrink-0 mt-0.5" />
            <p className="text-[15px] text-ink/80">
              This is the <strong>only</strong> action that resets the member-numbering sequence back to the start.
              Members removed through Suspend &amp; Remove keep their number permanently retired — numbering is never
              reused by any other path.
            </p>
          </div>
          <label className="text-[14px] font-semibold">Type <span className="font-mono text-maroon">DELETE ALL</span> to confirm</label>
          <input data-testid="delete-all-confirmation-input" value={deleteAllConfirm} onChange={(e) => setDeleteAllConfirm(e.target.value)}
            placeholder="DELETE ALL" className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px] mt-2 mb-4" />
          <button data-testid="delete-all-submit-button" onClick={editMode ? undefined : deleteAll}
            disabled={editMode ? false : (deleteAllConfirm.trim().toUpperCase() !== "DELETE ALL")}
            className="bg-maroon text-cream font-semibold rounded-lg px-6 py-3 min-h-[48px] text-[15px] disabled:opacity-40">
            <Txt id="reset.deleteall.btn" screen="Reset system data">Delete all data &amp; reset numbering</Txt>
          </button>
        </div>
      </div>
    </div>
  );
}
